import { useEffect, useState } from 'react';
import { get_api_url } from './urls';
import { get_api } from '../redux/api_funcs';

export interface ActivePackage {
  id: number;
  uuid: string;
  name: string;
  description: string;
  sessions_per_week: number;
}

const STORAGE_KEY = 'hridaya_active_packages_cache';
// Active packages are a near-static catalog, so a short TTL keeps the list
// fresh enough for ops while avoiding a network call on every modal open.
const CACHE_TTL_MS = 30 * 60 * 1000; // 30 minutes

// In-memory cache lives outside the React tree, so it survives component
// unmount/remount (e.g. switching tabs in the student profile) for the
// lifetime of the SPA session. inflightRequest dedupes concurrent callers.
let cachedPackages: ActivePackage[] | null = null;
let inflightRequest: Promise<ActivePackage[]> | null = null;

const readFromStorage = (): ActivePackage[] | null => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !Array.isArray(parsed.packages) || typeof parsed.ts !== 'number') {
      return null;
    }
    if (Date.now() - parsed.ts > CACHE_TTL_MS) return null;
    return parsed.packages;
  } catch {
    return null;
  }
};

const writeToStorage = (packages: ActivePackage[]) => {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ ts: Date.now(), packages }));
  } catch {
    // sessionStorage may be unavailable (private mode / quota) — in-memory cache still applies.
  }
};

const fetchActivePackages = (): Promise<ActivePackage[]> => {
  if (cachedPackages) return Promise.resolve(cachedPackages);

  const stored = readFromStorage();
  if (stored) {
    cachedPackages = stored;
    return Promise.resolve(stored);
  }

  if (inflightRequest) return inflightRequest;

  const url = get_api_url('active_packages');
  inflightRequest = get_api(url, true)
    .then((response: { data?: { packages?: ActivePackage[] } }) => {
      const pkgs: ActivePackage[] = response?.data?.packages || [];
      cachedPackages = pkgs;
      writeToStorage(pkgs);
      return pkgs;
    })
    .finally(() => {
      inflightRequest = null;
    });

  return inflightRequest;
};

// Invalidate the cache (e.g. after a package is edited) so the next read refetches.
export const clearActivePackagesCache = () => {
  cachedPackages = null;
  inflightRequest = null;
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore — nothing persisted to clear.
  }
};

/**
 * Returns the active package catalog, fetching it from the network only when it
 * is not already cached. The result is shared across every consumer for the SPA
 * session, so switching tabs / reopening modals reuses the cached list instead
 * of refetching `/cms/api/active_packages`.
 */
export const useActivePackages = () => {
  const [packages, setPackages] = useState<ActivePackage[]>(cachedPackages || []);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;

    fetchActivePackages()
      .then((pkgs) => {
        if (active) setPackages(pkgs);
      })
      .catch((e) => {
        // get_api re-rejects without logging, so keep the real cause (401 expired token,
        // 5xx, timeout) for debugging — the generic operator message can't distinguish them.
        console.error('active_packages fetch failed', e);
        if (active) setError('Failed to load package options.');
      });

    return () => {
      active = false;
    };
  }, []);

  return { packages, error };
};
